import React, { createContext, useContext, useState, useCallback } from 'react';
import Papa from 'papaparse';
import { Scenario, Category, TestResult, Project } from '../types';
import { useToast } from './ToastContext';
import { useNavigation } from './NavigationContext';
import {
  groupScenariosByCategory,
  generateUniqueId,
  extractCategories as extractCategoriesUtil,
} from '../utils/scenarioUtils';

const API_BASE_URL = process.env.REACT_APP_API_HOST;

export interface TestSessionContextValue {
  scenarios: Scenario[];
  categories: Category[];
  currentScenario: Scenario | null;
  currentTest: TestResult | null;
  testResults: TestResult[];
  selectedFile: File | null;
  error: string | null;
  isTestInfoModalOpen: boolean;
  isCSVEditorOpen: boolean;
  csvContent: string;
  isScenarioEditorOpen: boolean;
  scenarioToEdit: Scenario | null;
  setScenarios: React.Dispatch<React.SetStateAction<Scenario[]>>;
  setCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  setCurrentScenario: React.Dispatch<React.SetStateAction<Scenario | null>>;
  setCurrentTest: React.Dispatch<React.SetStateAction<TestResult | null>>;
  setTestResults: React.Dispatch<React.SetStateAction<TestResult[]>>;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setIsTestInfoModalOpen: (open: boolean) => void;
  setIsCSVEditorOpen: (open: boolean) => void;
  setCSVContent: (content: string) => void;
  setIsScenarioEditorOpen: (open: boolean) => void;
  setScenarioToEdit: (scenario: Scenario | null) => void;
  loadScenarios: (filename: string) => void;
  loadTestResults: () => Promise<void>;
  handleStartNewTest: (file: File) => void;
  handleNewTestClick: () => void;
  handleTestInfoSubmit: (data: {
    systemName: string;
    testerName: string;
    environment: string;
    version?: string;
    selectedCategories?: string[];
    isFullRegression: boolean;
    platforms: ('iOS' | 'Android' | 'Web')[];
  }) => Promise<void>;
  continueTest: (result: TestResult) => Promise<void>;
  handleStatusChange: (status: string) => Promise<void>;
  handleNotesChange: (notes: string) => Promise<void>;
  handleCategoriesUpdate: (categories: Category[]) => void;
  handleEditCSV: () => Promise<void>;
  handleSaveCSV: (newContent: string) => Promise<void>;
  handleEditScenario: (scenario: Scenario) => void;
  handleSaveScenario: (updatedScenario: Scenario) => Promise<void>;
  handleAddScenario: (newScenario: Partial<Scenario>) => Promise<void>;
  handleSaveTest: (test: TestResult) => Promise<void>;
  extractCategories: (scenarios: Scenario[]) => string[];
  handleScenarioSelect: (scenario: Scenario) => void;
  handleTestUpdate: (updatedTest: TestResult) => void;
  handleDeleteTest: (testId: string) => Promise<void>;
  handleDeleteScenarioFromTest: (testId: string, scenarioId: string) => Promise<void>;
  handleDeleteScenario: (testId: string, scenarioId: string) => Promise<void>;
  handleDeleteCategory: (categoryName: string) => Promise<void>;
  handleScenarioStatusChange: (scenarioId: string, newStatus: string) => Promise<void>;
  handleScenarioNoteChange: (scenarioId: string, newNote: string) => Promise<void>;
}

const TestSessionContext = createContext<TestSessionContextValue | null>(null);

export const TestSessionProvider: React.FC<{
  children: React.ReactNode;
  selectedProject: Project | null;
}> = ({ children, selectedProject }) => {
  const { showToast } = useToast();
  const { setCurrentScreen } = useNavigation();

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  const [currentTest, setCurrentTest] = useState<TestResult | null>(null);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isTestInfoModalOpen, setIsTestInfoModalOpen] = useState(false);
  const [isCSVEditorOpen, setIsCSVEditorOpen] = useState(false);
  const [csvContent, setCSVContent] = useState('');
  const [isScenarioEditorOpen, setIsScenarioEditorOpen] = useState(false);
  const [scenarioToEdit, setScenarioToEdit] = useState<Scenario | null>(null);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }), []);

  const loadScenarios = useCallback(
    (filename: string) => {
      fetch(`${API_BASE_URL}/api/tests/scenarios/${filename}`, { headers: getAuthHeaders() })
        .then(res => {
          if (!res.ok) throw new Error(`Błąd HTTP: ${res.status}`);
          return res.text();
        })
        .then(csvData => {
          const firstLine = csvData.split('\n')[0];
          const delimiter = firstLine.includes(';') ? ';' : ',';
          Papa.parse(csvData, {
            header: true,
            delimiter,
            complete: async (results) => {
              if (!results.data?.length) throw new Error('Plik CSV jest pusty lub nie zawiera danych');
              const parsed = (results.data as Scenario[])
                .filter(s => s.isDeleted !== 'true')
                .map(s => ({ ...s, Status: 'Not Tested', Notes: '' }));
              setScenarios(parsed);
              setSelectedFile(new File([], filename));
              setCategories(groupScenariosByCategory(parsed));
              try {
                const r = await fetch(
                  `${API_BASE_URL}/api/tests/test_results?filename=${filename}`,
                  { headers: getAuthHeaders() }
                );
                if (!r.ok) throw new Error('Nie udało się pobrać wyników testów');
                const data = await r.json();
                setTestResults(data);
              } catch (e) {
                setError('Nie udało się załadować wyników testów');
              }
            },
            error: () => showToast('Błąd podczas parsowania CSV. Sprawdź format pliku.', 'error'),
          });
        })
        .catch(() =>
          showToast('Nie udało się załadować pliku CSV. Sprawdź czy plik istnieje i jest dostępny.', 'error')
        );
    },
    [getAuthHeaders, showToast]
  );

  const loadTestResults = useCallback(async () => {
    try {
      const url = `${API_BASE_URL}/api/tests/test_results${selectedFile ? `?filename=${selectedFile.name}` : ''}`;
      const r = await fetch(url, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error('Nie udało się pobrać wyników testów');
      const data = await r.json();
      setTestResults(data);
    } catch {
      setError('Nie udało się załadować wyników testów');
    }
  }, [selectedFile, getAuthHeaders]);

  const handleStartNewTest = useCallback(
    (file: File) => {
      setScenarios([]);
      setCategories([]);
      setCurrentTest(null);
      setCurrentScenario(null);
      setError(null);
      if (!file.name) {
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      loadScenarios(file.name);
    },
    [loadScenarios]
  );

  const handleNewTestClick = useCallback(() => {
    if (!selectedFile) {
      showToast('Najpierw wybierz plik CSV', 'info');
      return;
    }
    setIsTestInfoModalOpen(true);
  }, [selectedFile, showToast]);

  const handleSaveTest = useCallback(
    async (test: TestResult) => {
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/tests/test_results/${test.metadata.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(test),
          }
        );
        if (!r.ok) throw new Error('Failed to update test');
      } catch {
        showToast('Nie udało się zapisać zmian. Spróbuj ponownie.', 'error');
      }
    },
    [getAuthHeaders, showToast]
  );

  const handleTestInfoSubmit = useCallback(
    async (data: {
      systemName: string;
      testerName: string;
      environment: string;
      version?: string;
      selectedCategories?: string[];
      isFullRegression: boolean;
      platforms: ('iOS' | 'Android' | 'Web')[];
    }) => {
      if (!selectedFile) return;
      try {
        const testId = generateUniqueId();
        const res = await fetch(
          `${API_BASE_URL}/api/tests/scenarios/${selectedFile.name}`,
          { headers: getAuthHeaders() }
        );
        const csvData = await res.text();
        const parseResult = await new Promise<Papa.ParseResult<Scenario>>(resolve =>
          Papa.parse<Scenario>(csvData, { header: true, complete: r => resolve(r as Papa.ParseResult<Scenario>) })
        );
        const initialScenarioResults: Record<string, { Status: string; Notes: string }> = {};
        parseResult.data.forEach(s => {
          if (s['ID testu']) initialScenarioResults[s['ID testu']] = { Status: 'Not Tested', Notes: '' };
        });
        const csvFileId = selectedProject?.csvFiles.find(f => f.originalName === selectedFile.name)?.id;
        const testResult: TestResult = {
          filename: selectedFile.name,
          scenarioResults: initialScenarioResults,
          metadata: {
            ...data,
            startDate: new Date().toISOString(),
            id: testId,
            projectId: selectedProject?.id,
            csvFileId,
          },
        };
        const saveRes = await fetch(
          `${API_BASE_URL}/api/tests/test_results/${testId}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(testResult),
          }
        );
        if (!saveRes.ok) throw new Error('Nie udało się zapisać wyników testu');
        if (selectedProject?.id && csvFileId) {
          await fetch(`${API_BASE_URL}/api/projects/${selectedProject.id}/update`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ csvFileId, testId, action: 'add' }),
          });
        }
        localStorage.setItem('lastTestId', testId);
        setCurrentTest(testResult);
        setCurrentScreen('test');
        setIsTestInfoModalOpen(false);
      } catch (e) {
        showToast('Nie udało się utworzyć testu', 'error');
      }
    },
    [selectedFile, selectedProject, getAuthHeaders, showToast, setCurrentScreen]
  );

  const continueTest = useCallback(
    async (result: TestResult) => {
      try {
        localStorage.setItem('lastTestId', result.metadata.id);
        const res = await fetch(
          `${API_BASE_URL}/api/tests/scenarios/${result.filename}`,
          { headers: getAuthHeaders() }
        );
        const csvData = await res.text();
        Papa.parse(csvData, {
          header: true,
          complete: (results) => {
            let allScenarios = results.data as Scenario[];
            if (
              result.metadata.selectedCategories?.length &&
              !result.metadata.isFullRegression
            ) {
              allScenarios = allScenarios.filter(s =>
                result.metadata.selectedCategories!.includes(s.Kategoria)
              );
            }
            const withResults = allScenarios
              .filter(s => result.scenarioResults[s['ID testu']] !== undefined && s.isDeleted !== 'true')
              .map(s => ({
                ...s,
                Status: result.scenarioResults[s['ID testu']].Status,
                Notes: result.scenarioResults[s['ID testu']].Notes,
              }));
            setScenarios(withResults);
            setCategories(groupScenariosByCategory(withResults));
            setCurrentTest(result);
            setCurrentScreen('test');
          },
        });
      } catch {
        showToast('Nie udało się wczytać pliku CSV', 'error');
      }
    },
    [getAuthHeaders, showToast, setCurrentScreen]
  );

  const handleStatusChange = useCallback(
    async (status: string) => {
      if (!currentScenario || !currentTest) return;
      const scenarioId = currentScenario['ID testu'];
      setCurrentScenario(prev => (prev ? { ...prev, Status: status } : null));
      const updatedTest = {
        ...currentTest,
        scenarioResults: {
          ...currentTest.scenarioResults,
          [scenarioId]: { ...currentTest.scenarioResults[scenarioId], Status: status },
        },
      };
      setCurrentTest(updatedTest);
      setTestResults(prev =>
        prev.map(r => (r.metadata.id === updatedTest.metadata.id ? updatedTest : r))
      );
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/tests/test_results/${updatedTest.metadata.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(updatedTest),
          }
        );
        if (!r.ok) throw new Error('Failed to update test');
        setCategories(prev =>
          prev.map(cat => ({
            ...cat,
            scenarios: cat.scenarios.map(s =>
              s['ID testu'] === scenarioId ? { ...s, Status: status } : s
            ),
          }))
        );
      } catch {
        showToast('Nie udało się zaktualizować statusu. Spróbuj ponownie.', 'error');
      }
    },
    [currentScenario, currentTest, getAuthHeaders, showToast]
  );

  const handleNotesChange = useCallback(
    async (notes: string) => {
      if (!currentScenario || !currentTest) return;
      const scenarioId = currentScenario['ID testu'];
      setCurrentScenario(prev => (prev ? { ...prev, Notes: notes } : null));
      const updatedTest = {
        ...currentTest,
        scenarioResults: {
          ...currentTest.scenarioResults,
          [scenarioId]: { ...currentTest.scenarioResults[scenarioId], Notes: notes },
        },
      };
      setCurrentTest(updatedTest);
      setTestResults(prev =>
        prev.map(r => (r.metadata.id === updatedTest.metadata.id ? updatedTest : r))
      );
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/tests/test_results/${updatedTest.metadata.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(updatedTest),
          }
        );
        if (!r.ok) throw new Error('Failed to update test');
        setCategories(prev =>
          prev.map(cat => ({
            ...cat,
            scenarios: cat.scenarios.map(s =>
              s['ID testu'] === scenarioId ? { ...s, Notes: notes } : s
            ),
          }))
        );
      } catch {
        showToast('Nie udało się zapisać notatki. Spróbuj ponownie.', 'error');
      }
    },
    [currentScenario, currentTest, getAuthHeaders, showToast]
  );

  const handleCategoriesUpdate = useCallback((updated: Category[]) => setCategories(updated), []);

  const handleEditCSV = useCallback(async () => {
    if (!selectedFile) return;
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/tests/scenarios/${selectedFile.name}`,
        { headers: getAuthHeaders() }
      );
      if (!r.ok) throw new Error(`Błąd HTTP: ${r.status}`);
      const content = await r.text();
      setCSVContent(content);
      setIsCSVEditorOpen(true);
    } catch {
      showToast('Nie udało się wczytać pliku CSV do edycji.', 'error');
    }
  }, [selectedFile, getAuthHeaders, showToast]);

  const handleSaveCSV = useCallback(
    async (newContent: string) => {
      if (!selectedFile) return;
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/tests/scenarios/${selectedFile.name}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'text/csv', ...getAuthHeaders() },
            body: newContent,
          }
        );
        if (!r.ok) throw new Error(`Błąd HTTP: ${r.status}`);
        loadScenarios(selectedFile.name);
        setIsCSVEditorOpen(false);
        showToast('Plik CSV został zapisany.', 'success');
      } catch {
        showToast('Nie udało się zapisać zmian w pliku CSV.', 'error');
      }
    },
    [selectedFile, loadScenarios, getAuthHeaders, showToast]
  );

  const handleEditScenario = useCallback((scenario: Scenario) => {
    setScenarioToEdit(scenario);
    setIsScenarioEditorOpen(true);
  }, []);

  const handleSaveScenario = useCallback(
    async (updatedScenario: Scenario) => {
      const filename = selectedFile?.name || currentTest?.filename;
      if (!filename) {
        showToast('Nie można określić pliku CSV do zapisu', 'error');
        return;
      }
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/tests/scenarios/${filename}`,
          { headers: getAuthHeaders() }
        );
        if (!r.ok) throw new Error(`Błąd HTTP: ${r.status}`);
        const csvData = await r.text();
        Papa.parse(csvData, {
          header: true,
          complete: async (results) => {
            const scenariosData = results.data as Scenario[];
            const updated = scenariosData.map(s =>
              s['ID testu'] === updatedScenario['ID testu']
                ? (() => {
                    const { Status, Notes, 'ID testu': _, ...rest } = updatedScenario;
                    return { 'ID testu': s['ID testu'], ...rest };
                  })()
                : (() => {
                    const { Status, Notes, ...rest } = s;
                    return rest;
                  })()
            );
            const csv = Papa.unparse(updated);
            const saveRes = await fetch(
              `${API_BASE_URL}/api/tests/scenarios/${filename}`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'text/csv', ...getAuthHeaders() },
                body: csv,
              }
            );
            if (!saveRes.ok) throw new Error('Błąd HTTP');
            const withStatus = updated.map(s => ({
              ...s,
              Status: currentTest?.scenarioResults?.[s['ID testu']]?.Status || 'Not Tested',
              Notes: currentTest?.scenarioResults?.[s['ID testu']]?.Notes || '',
            }));
            setScenarios(withStatus);
            setCategories(groupScenariosByCategory(withStatus));
            if (currentScenario?.['ID testu'] === updatedScenario['ID testu']) {
              setCurrentScenario(prev =>
                prev ? { ...updatedScenario, Status: prev.Status, Notes: prev.Notes } : null
              );
            }
            setIsScenarioEditorOpen(false);
            setScenarioToEdit(null);
          },
          error: () =>
            showToast('Błąd podczas parsowania CSV. Sprawdź format pliku.', 'error'),
        });
      } catch {
        showToast('Nie udało się zapisać zmian w scenariuszu.', 'error');
      }
    },
    [selectedFile, currentTest, currentScenario, getAuthHeaders, showToast]
  );

  const handleAddScenario = useCallback(
    async (newScenario: Partial<Scenario>) => {
      const filename = selectedFile?.name || currentTest?.filename;
      if (!filename) {
        showToast('Nie można określić pliku CSV do zapisu', 'error');
        return;
      }
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/tests/scenarios/${filename}`,
          { headers: getAuthHeaders() }
        );
        if (!r.ok) throw new Error(`Błąd HTTP: ${r.status}`);
        const csvData = await r.text();
        Papa.parse(csvData, {
          header: true,
          complete: async (results) => {
            const allScenarios = results.data as Scenario[];
            const existingIds = allScenarios
              .map(s => {
                const m = s['ID testu'].match(/([A-Z]+)-(\d+)/);
                return m ? parseInt(m[2]) : 0;
              })
              .filter(n => !isNaN(n));
            const maxId = Math.max(...existingIds, 0);
            let prefix = 'TEST';
            if (newScenario['ID testu']) {
              const pm = newScenario['ID testu'].match(/^([A-Z]+)-/);
              if (pm) prefix = pm[1];
            } else if (newScenario['Kategoria']) {
              prefix = newScenario['Kategoria'].toUpperCase().substring(0, 3);
            }
            const newId = `${prefix}-${String(maxId + 1).padStart(3, '0')}`;
            const { Status, Notes, ...rest } = newScenario as Scenario;
            const newScenarioWithId: Scenario = {
              ...rest,
              'ID testu': newId,
              isDeleted: 'false',
            };
            const updated = [...allScenarios, newScenarioWithId];
            const csv = Papa.unparse(updated, {
              header: true,
              columns: [
                'ID testu',
                'Kategoria',
                'Nazwa testu',
                'Kroki testowe',
                'Oczekiwany rezultat',
                'isDeleted',
              ],
            });
            const saveRes = await fetch(
              `${API_BASE_URL}/api/tests/scenarios/${filename}`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'text/csv', ...getAuthHeaders() },
                body: csv,
              }
            );
            if (!saveRes.ok) throw new Error('Błąd HTTP');
            const active = updated.filter(s => s.isDeleted !== 'true');
            const withStatus = active.map(s => {
              if (currentTest?.scenarioResults[s['ID testu']])
                return {
                  ...s,
                  Status: currentTest.scenarioResults[s['ID testu']].Status,
                  Notes: currentTest.scenarioResults[s['ID testu']].Notes,
                };
              if (s['ID testu'] === newId)
                return { ...s, Status: 'Not Tested', Notes: '' };
              return { ...s, Status: s.Status || 'Not Tested', Notes: s.Notes || '' };
            });
            setScenarios(withStatus);
            setCategories(groupScenariosByCategory(withStatus));
            if (currentTest) {
              setCurrentTest(prev =>
                prev
                  ? {
                      ...prev,
                      scenarioResults: {
                        ...prev.scenarioResults,
                        [newId]: { Status: 'Not Tested', Notes: '' },
                      },
                    }
                  : null
              );
            }
            showToast(`Scenariusz "${newScenario['Nazwa testu']}" został dodany pomyślnie.`, 'success');
          },
          error: () =>
            showToast('Błąd podczas parsowania CSV. Sprawdź format pliku.', 'error'),
        });
      } catch {
        showToast('Nie udało się dodać nowego scenariusza.', 'error');
      }
    },
    [selectedFile, currentTest, getAuthHeaders, showToast]
  );

  const handleScenarioSelect = useCallback((scenario: Scenario) => setCurrentScenario(scenario), []);

  const handleTestUpdate = useCallback((updatedTest: TestResult) => {
    setTestResults(prev =>
      prev.map(r => (r.metadata.id === updatedTest.metadata.id ? updatedTest : r))
    );
    setCurrentTest(updatedTest);
  }, []);

  const handleDeleteTest = useCallback(
    async (testId: string) => {
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/tests/test_results/${testId}`,
          { method: 'DELETE', headers: getAuthHeaders() }
        );
        if (!r.ok) throw new Error(`Błąd podczas usuwania testu: ${r.status}`);
        if (selectedProject) {
          const csvFileId = selectedProject.csvFiles.find(f => f.testIds.includes(testId))?.id;
          if (csvFileId) {
            await fetch(`${API_BASE_URL}/api/projects/${selectedProject.id}/update`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
              body: JSON.stringify({ csvFileId, testId, action: 'remove' }),
            });
          }
        }
        setTestResults(prev => prev.filter(r => r.metadata.id !== testId));
        setCurrentTest(null);
        setCurrentScreen('home');
        showToast('Test został pomyślnie usunięty', 'success');
      } catch {
        showToast('Wystąpił błąd podczas usuwania testu. Spróbuj ponownie.', 'error');
      }
    },
    [selectedProject, getAuthHeaders, showToast, setCurrentScreen]
  );

  const handleDeleteScenarioFromTest = useCallback(
    async (testId: string, scenarioId: string) => {
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/tests/test_results/${testId}`,
          { headers: getAuthHeaders() }
        );
        if (!r.ok) throw new Error('Błąd podczas pobierania testu');
        const test = await r.json();
        const { [scenarioId]: _, ...remaining } = test.scenarioResults;
        const updatedTest = { ...test, scenarioResults: remaining };
        await handleSaveTest(updatedTest);
        setTestResults(prev =>
          prev.map(res => (res.metadata.id === testId ? updatedTest : res))
        );
        if (currentTest?.metadata.id === testId) {
          setCurrentTest(updatedTest);
          const updatedScenarios = scenarios.filter(s => s['ID testu'] !== scenarioId);
          setScenarios(updatedScenarios);
          setCategories(groupScenariosByCategory(updatedScenarios));
          if (currentScenario?.['ID testu'] === scenarioId) setCurrentScenario(null);
        }
        showToast('Scenariusz został pomyślnie usunięty z testu', 'success');
      } catch {
        showToast('Wystąpił błąd podczas usuwania scenariusza z testu. Spróbuj ponownie.', 'error');
      }
    },
    [currentTest, currentScenario, scenarios, getAuthHeaders, handleSaveTest, showToast]
  );

  const handleDeleteScenario = useCallback(
    async (testId: string, scenarioId: string) => {
      const filename = selectedFile?.name || currentTest?.filename;
      if (!filename) {
        showToast('Nie można określić pliku CSV do usunięcia', 'error');
        return;
      }
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/tests/scenarios/${filename}`,
          { headers: getAuthHeaders() }
        );
        if (!r.ok) throw new Error('Błąd HTTP');
        const csvData = await r.text();
        Papa.parse(csvData, {
          header: true,
          complete: async (results) => {
            const scenariosData = results.data as Scenario[];
            const updated = scenariosData.map(s =>
              s['ID testu'] === scenarioId ? { ...s, isDeleted: 'true' as const } : s
            );
            const withDeleted = updated.map(s => ({
              ...s,
              isDeleted: s.isDeleted || 'false',
            }));
            const csv = Papa.unparse(withDeleted, {
              header: true,
              columns: [
                'ID testu',
                'Kategoria',
                'Nazwa testu',
                'Kroki testowe',
                'Oczekiwany rezultat',
                'isDeleted',
              ],
            });
            const saveRes = await fetch(
              `${API_BASE_URL}/api/tests/scenarios/${filename}`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'text/csv', ...getAuthHeaders() },
                body: csv,
              }
            );
            if (!saveRes.ok) throw new Error('Błąd HTTP');
            if (currentTest) await handleDeleteScenarioFromTest(currentTest.metadata.id, scenarioId);
            setScenarios(prev => prev.filter(s => s['ID testu'] !== scenarioId));
            setCategories(prev =>
              prev.map(cat => ({
                ...cat,
                scenarios: cat.scenarios.filter(s => s['ID testu'] !== scenarioId),
              }))
            );
            if (currentScenario?.['ID testu'] === scenarioId) setCurrentScenario(null);
            showToast('Scenariusz został pomyślnie usunięty', 'success');
          },
          error: () =>
            showToast('Błąd podczas parsowania CSV. Sprawdź format pliku.', 'error'),
        });
      } catch {
        showToast('Nie można usunąć scenariusza. Spróbuj ponownie.', 'error');
      }
    },
    [
      selectedFile,
      currentTest,
      currentScenario,
      getAuthHeaders,
      handleDeleteScenarioFromTest,
      showToast,
    ]
  );

  const handleDeleteCategory = useCallback(
    async (categoryName: string) => {
      if (!currentTest) {
        showToast('Brak aktywnego testu', 'error');
        return;
      }
      const category = categories.find(c => c.name === categoryName);
      if (!category?.scenarios.length) {
        showToast('Kategoria nie zawiera scenariuszy do usunięcia', 'warning');
        return;
      }
      const scenarioIds = category.scenarios.map(s => s['ID testu']);
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/tests/test_results/${currentTest.metadata.id}/category/${encodeURIComponent(categoryName)}`,
          { method: 'DELETE', headers: getAuthHeaders() }
        );
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || `Błąd HTTP: ${r.status}`);
        }
        const result = await r.json();
        const updatedR = await fetch(
          `${API_BASE_URL}/api/tests/test_results/${currentTest.metadata.id}`,
          { headers: getAuthHeaders() }
        );
        if (!updatedR.ok) throw new Error('Nie udało się pobrać zaktualizowanego testu');
        const updatedTest = await updatedR.json();
        setCurrentTest(updatedTest);
        setScenarios(prev => prev.filter(s => !scenarioIds.includes(s['ID testu'])));
        setCategories(prev =>
          prev.map(cat =>
            cat.name === categoryName
              ? { ...cat, scenarios: [] }
              : {
                  ...cat,
                  scenarios: cat.scenarios.filter(s => !scenarioIds.includes(s['ID testu'])),
                }
          )
        );
        if (currentScenario && scenarioIds.includes(currentScenario['ID testu']))
          setCurrentScenario(null);
        setTestResults(prev =>
          prev.map(r => (r.metadata.id === currentTest.metadata.id ? updatedTest : r))
        );
        showToast(
          `Kategoria "${categoryName}" została pomyślnie usunięta (${result.totalScenarios} scenariuszy)`,
          'success'
        );
      } catch (e) {
        showToast(
          e instanceof Error ? e.message : 'Nie można usunąć kategorii. Spróbuj ponownie.',
          'error'
        );
      }
    },
    [currentTest, currentScenario, categories, getAuthHeaders, showToast]
  );

  const handleScenarioStatusChange = useCallback(
    async (scenarioId: string, newStatus: string) => {
      if (!currentTest) return;
      const updated = { ...currentTest };
      const scenario = updated.scenarioResults[scenarioId];
      if (scenario) {
        scenario.Status = newStatus;
        await handleSaveTest(updated);
      }
    },
    [currentTest, handleSaveTest]
  );

  const handleScenarioNoteChange = useCallback(
    async (scenarioId: string, newNote: string) => {
      if (!currentTest) return;
      const updated = { ...currentTest };
      const scenario = updated.scenarioResults[scenarioId];
      if (scenario) {
        scenario.Notes = newNote;
        await handleSaveTest(updated);
      }
    },
    [currentTest, handleSaveTest]
  );

  const extractCategories = useCallback(extractCategoriesUtil, []);

  const value: TestSessionContextValue = {
    scenarios,
    categories,
    currentScenario,
    currentTest,
    testResults,
    selectedFile,
    error,
    isTestInfoModalOpen,
    isCSVEditorOpen,
    csvContent,
    isScenarioEditorOpen,
    scenarioToEdit,
    setScenarios,
    setCategories,
    setCurrentScenario,
    setCurrentTest,
    setTestResults,
    setSelectedFile,
    setError,
    setIsTestInfoModalOpen,
    setIsCSVEditorOpen,
    setCSVContent,
    setIsScenarioEditorOpen,
    setScenarioToEdit,
    loadScenarios,
    loadTestResults,
    handleStartNewTest,
    handleNewTestClick,
    handleTestInfoSubmit,
    continueTest,
    handleStatusChange,
    handleNotesChange,
    handleCategoriesUpdate,
    handleEditCSV,
    handleSaveCSV,
    handleEditScenario,
    handleSaveScenario,
    handleAddScenario,
    handleSaveTest,
    extractCategories,
    handleScenarioSelect,
    handleTestUpdate,
    handleDeleteTest,
    handleDeleteScenarioFromTest,
    handleDeleteScenario,
    handleDeleteCategory,
    handleScenarioStatusChange,
    handleScenarioNoteChange,
  };

  return (
    <TestSessionContext.Provider value={value}>{children}</TestSessionContext.Provider>
  );
};

export function useTestSession(): TestSessionContextValue {
  const ctx = useContext(TestSessionContext);
  if (!ctx) throw new Error('useTestSession must be used within TestSessionProvider');
  return ctx;
}
